import React, { useState, useEffect, useMemo } from 'react';
import type { Visitation, VisitationFormData } from '../../types/visitation';
import { useNotification } from '../../contexts/NotificationContext';
import { visitationService } from '../../services/visitation.service';
import { FaUser, FaCalendarAlt, FaUsers, FaClipboardList, FaCheckCircle, FaExclamationTriangle, FaSave, FaTimes, FaHome, FaLock } from 'react-icons/fa';
import { usePermissions } from '../../hooks/usePermissions';

interface VisitationFormProps {
  visitation?: Visitation | null;
  onSuccess: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  readOnly?: boolean;
}

const VisitationForm: React.FC<VisitationFormProps> = ({
  visitation,
  onSuccess,
  onCancel,
  isLoading = false,
  readOnly = false
}) => {
  const { canWrite } = usePermissions();
  const isReadOnly = readOnly || !canWrite('visitations');
  const [formData, setFormData] = useState<VisitationFormData>({
    memberId: '',
    memberName: '',
    visitDate: new Date().toISOString().split('T')[0],
    visitors: '',
    type: '정기',
    status: '예정',
    notes: '',
    followUpActions: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addNotification } = useNotification();

  // 심방 유형 및 상태 옵션
  const visitationTypes = useMemo(() => [
    { value: '정기 심방', label: '정기 심방', icon: <FaCalendarAlt className="h-4 w-4" /> },
    { value: '특별 심방', label: '특별 심방', icon: <FaUsers className="h-4 w-4" /> },
    { value: '긴급 심방', label: '긴급 심방', icon: <FaExclamationTriangle className="h-4 w-4" /> },
    { value: '신입 심방', label: '신입 심방', icon: <FaHome className="h-4 w-4" /> },
    { value: '기타', label: '기타', icon: <FaClipboardList className="h-4 w-4" /> },
  ], []);

  const visitationStatuses = useMemo(() => [
    { value: '예정', label: '예정', color: 'blue' },
    { value: '완료', label: '완료', color: 'green' },
    { value: '후속조치 필요', label: '후속조치 필요', color: 'yellow' },
    { value: '취소', label: '취소', color: 'red' },
  ], []);

  useEffect(() => {
    if (visitation) {
      setFormData({
        memberId: visitation.memberId,
        memberName: visitation.memberName,
        visitDate: visitation.visitDate.toDate().toISOString().split('T')[0],
        visitors: Array.isArray(visitation.visitors) ? visitation.visitors.join(', ') : visitation.visitors,
        type: visitation.type,
        status: visitation.status,
        notes: visitation.notes,
        followUpActions: visitation.followUpActions || '',
      });
    } else {
      setFormData({
        memberId: '',
        memberName: '',
        visitDate: new Date().toISOString().split('T')[0],
        visitors: '',
        type: '정기',
        status: '예정',
        notes: '',
        followUpActions: '',
      });
    }
  }, [visitation]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.memberName) {
      addNotification('심방 대상자 이름을 입력해주세요.', 'error');
      setIsSubmitting(false);
      return;
    }

    try {
      if (visitation && visitation.id) {
        await visitationService.updateVisitation(visitation.id, formData);
        addNotification('심방 기록이 성공적으로 수정되었습니다.', 'success');
      } else {
        await visitationService.addVisitation(formData);
        addNotification('새 심방 기록이 성공적으로 추가되었습니다.', 'success');
      }
      onSuccess();
    } catch (error) {
      console.error("Failed to save visitation", error);
      addNotification(visitation ? '심방 기록 수정 실패' : '심방 기록 추가 실패', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusStyle = (color: string) => {
    switch (color) {
      case 'blue':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'green':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'yellow':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'red':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
      {/* 헤더 */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white">
              <FaHome />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {visitation ? '심방 기록 수정' : '새 심방 기록 추가'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {visitation ? '기존 심방 기록을 수정합니다' : '새로운 심방 기록을 등록합니다'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting || isLoading}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-150"
            aria-label="취소"
          >
            <FaTimes size={20} />
          </button>
        </div>
      </div>

      {/* 읽기 전용 모드 알림 */}
      {isReadOnly && (
        <div className="mx-6 mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
          <div className="flex items-center space-x-2">
            <FaLock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              읽기 전용 모드입니다. 심방 정보를 수정할 권한이 없습니다.
            </span>
          </div>
        </div>
      )}
      
      {/* 폼 내용 */}
      <form onSubmit={isReadOnly ? undefined : handleSubmit} className="p-6 space-y-6">
        {/* 기본 정보 섹션 */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <FaUser className="mr-2" />
            기본 정보
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="memberName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                심방 대상자 <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaUser className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="memberName"
                  id="memberName"
                  value={formData.memberName}
                  onChange={handleChange}
                  placeholder="대상자 이름을 입력하세요"
                  lang="ko"
                  inputMode="text"
                  disabled={isReadOnly}
                  className={`block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="visitDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                심방 날짜 <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaCalendarAlt className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="date"
                  name="visitDate"
                  id="visitDate"
                  value={formData.visitDate}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  className={`block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="visitors" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                방문자 <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaUsers className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="visitors"
                  id="visitors"
                  value={formData.visitors}
                  onChange={handleChange}
                  placeholder="예: 김목사, 이장로"
                  lang="ko"
                  inputMode="text"
                  disabled={isReadOnly}
                  className={`block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                심방 유형 <span className="text-red-500">*</span>
              </label>
              <select
                name="type"
                id="type"
                value={formData.type}
                onChange={handleChange}
                disabled={isReadOnly}
                className={`block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
              >
                {visitationTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    <span className="flex items-center">
                      {type.icon}
                      <span className="ml-2">{type.label}</span>
                    </span>
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                심방 상태 <span className="text-red-500">*</span>
              </label>
              <select
                name="status"
                id="status"
                value={formData.status}
                onChange={handleChange}
                disabled={isReadOnly}
                className={`block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
              >
                {visitationStatuses.map(status => (
                  <option key={status.value} value={status.value}>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(status.color)}`}>
                      {status.label}
                    </span>
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 상세 정보 섹션 */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <FaClipboardList className="mr-2" />
            상세 정보
          </h3>
          
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              심방 내용 <span className="text-red-500">*</span>
            </label>
            <textarea
              name="notes"
              id="notes"
              rows={4}
              value={formData.notes}
              onChange={handleChange}
              placeholder="심방 내용을 상세히 입력하세요"
              lang="ko"
              inputMode="text"
              disabled={isReadOnly}
              className={`block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
              required
            />
          </div>

          <div>
            <label htmlFor="followUpActions" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              후속 조치 사항
            </label>
            <textarea
              name="followUpActions"
              id="followUpActions"
              rows={3}
              value={formData.followUpActions}
              onChange={handleChange}
              placeholder="후속 조치가 필요한 사항이 있다면 입력하세요"
              lang="ko"
              inputMode="text"
              disabled={isReadOnly}
              className={`block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
            />
          </div>
        </div>

        {/* 버튼 영역 */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting || isLoading}
            className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150 disabled:opacity-50"
          >
            <FaTimes className="mr-2 h-4 w-4" />
            취소
          </button>
          {!isReadOnly && (
            <button
              type="submit"
              disabled={isSubmitting || isLoading}
              className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150 disabled:opacity-50"
            >
              <FaSave className="mr-2 h-4 w-4" />
              {isSubmitting ? (visitation ? '수정 중...' : '저장 중...') : (visitation ? '수정하기' : '저장하기')}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default VisitationForm;
